'use client';

import { useEffect, useState, type CSSProperties } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { slugifyHeading } from './wikiSlug';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * "On this page" table of contents — the right rail of a docs layout.
 * A pure client block: after the bound {@code EntityContent} body
 * renders, it scans the main content column ({@code contentSelector})
 * for headings, guarantees each has an {@code id} (so anchor links
 * work even when the CMS body omits them), and tracks the heading
 * nearest the top via {@link IntersectionObserver} to highlight the
 * active entry as the reader scrolls.
 *
 * <p>Decoupled from the body block by design — it targets a stable
 * wrapper class ({@code .wiki-docs-main}) rather than the RichText
 * internals, so operators can reorder the body without breaking the TOC.
 * Renders nothing when fewer than two headings exist (a lone entry isn't
 * worth a rail).
 */
export interface WikiTocProps {
  /** Rail heading. Default "On this page". */
  title?: string;
  /** CSS selector for the content column to scan. Default ".wiki-docs-main". */
  contentSelector?: string;
  /** Shallowest heading level included (2 = h2). Default 2. */
  minLevel?: number;
  /** Deepest heading level included (3 = h3). Default 3. */
  maxLevel?: number;
  /** Sticky offset from the top in px (clears a fixed header). Default 88. */
  stickyTop?: number;
}

interface TocItem {
  id: string;
  text: string;
  level: number;
}

export function WikiToc({
  title: authoredTitle,
  contentSelector = '.wiki-docs-main',
  minLevel = 2,
  maxLevel = 3,
  stickyTop = 88,
}: WikiTocProps) {
  const { t } = useTranslation('site-builder-blocks');
  const title = authoredTitle ?? t('wiki.toc.title');
  const pathname = usePathname() ?? '';
  const [items, setItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>('');

  // Scan the rendered body for headings once it's in the DOM. The body is
  // server-rendered HTML present at hydration, but a few frames of retry
  // covers the case where this rail mounts a tick before it.
  useEffect(() => {
    let raf = 0;
    let tries = 0;
    const selector = Array.from({ length: maxLevel - minLevel + 1 }, (_, i) => `h${minLevel + i}`)
      .map((h) => `${contentSelector} ${h}`)
      .join(',');

    const scan = () => {
      const container = document.querySelector(contentSelector);
      const heads = container ? Array.from(container.querySelectorAll(selector)) : [];
      if (heads.length === 0 && tries < 12) {
        tries += 1;
        raf = requestAnimationFrame(scan);
        return;
      }
      const seen = new Set<string>();
      const next: TocItem[] = [];
      for (const h of heads) {
        const el = h as HTMLElement;
        const text = (el.textContent ?? '').trim();
        if (!text) continue;
        let id = el.id || slugifyHeading(text);
        while (seen.has(id)) id = `${id}-1`;
        seen.add(id);
        if (!el.id) el.id = id;
        el.style.scrollMarginTop = `${stickyTop}px`;
        const level = Number(el.tagName.slice(1)) || minLevel;
        next.push({ id, text, level });
      }
      setItems(next);
    };

    scan();
    return () => cancelAnimationFrame(raf);
  }, [pathname, contentSelector, minLevel, maxLevel, stickyTop]);

  // Scroll-spy: the last heading to cross the top band is "active".
  useEffect(() => {
    if (items.length === 0) return;
    const els = items
      .map((it) => document.getElementById(it.id))
      .filter((el): el is HTMLElement => el != null);
    if (els.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target.id) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: `-${stickyTop}px 0px -70% 0px`, threshold: 0 },
    );
    for (const el of els) observer.observe(el);
    return () => observer.disconnect();
  }, [items, stickyTop]);

  if (items.length < 2) return null;

  return (
    <BlockRoot as="nav" aria-label={title} style={{ ...navStyle, top: stickyTop }}>
      <p style={titleStyle}>{title}</p>
      <ul style={listStyle}>
        {items.map((it) => (
          <li key={it.id} style={{ paddingLeft: (it.level - minLevel) * 12 }}>
            <a
              href={`#${it.id}`}
              aria-current={activeId === it.id ? 'true' : undefined}
              style={{
                ...linkStyle,
                color:
                  activeId === it.id
                    ? 'var(--color-text-brand, var(--color-brand-700, #2b3eb0))'
                    : 'var(--color-text-secondary, #71717a)',
                fontWeight: activeId === it.id ? 600 : 400,
                borderLeftColor:
                  activeId === it.id
                    ? 'var(--color-text-brand, var(--color-brand-600, #3b4fd0))'
                    : 'transparent',
              }}
            >
              {it.text}
            </a>
          </li>
        ))}
      </ul>
    </BlockRoot>
  );
}

const navStyle: CSSProperties = {
  position: 'sticky',
  alignSelf: 'start',
  fontSize: 'var(--font-size-sm, 13px)',
};

const titleStyle: CSSProperties = {
  margin: '0 0 var(--space-2, 8px)',
  fontSize: '12px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 'var(--font-tracking-wider, 0.06em)',
  color: 'var(--color-text-tertiary, #a1a1aa)',
};

const listStyle: CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const linkStyle: CSSProperties = {
  display: 'block',
  textDecoration: 'none',
  lineHeight: 1.4,
  padding: 'var(--space-1, 4px) 0 var(--space-1, 4px) var(--space-3, 12px)',
  borderLeft: '2px solid transparent',
};
